/**
 * Pre-fetches an image URL and returns a data URL suitable for
 * `@react-pdf/renderer`'s <Image src=...>. react-pdf's own image fetcher is
 * fragile with cross-origin assets (especially Firebase Storage) — it can
 * fail silently leaving a blank slot — so we inline every logo as a data URL
 * before handing it off.
 *
 * - SVG URLs: rasterized to PNG via canvas at 2x for crisp PDF output.
 * - Raster URLs (PNG/JPG/WebP): fetched and re-encoded as a data URL so the
 *   result is same-origin-safe regardless of the original host's CORS.
 * - null / failure: returns null so callers can skip the <Image>.
 */
export async function svgUrlToPngDataUrl(url, width, height) {
  if (!url) return null;

  const lower = url.toLowerCase();
  const isSvg = lower.endsWith(".svg");

  try {
    if (isSvg) {
      const res = await fetch(url);
      if (!res.ok) return null;
      const svgText = await res.text();
      const blob = new Blob([svgText], { type: "image/svg+xml" });
      const blobUrl = URL.createObjectURL(blob);

      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = blobUrl;
      });

      const canvas = document.createElement("canvas");
      canvas.width = width * 2;
      canvas.height = height * 2;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);

      URL.revokeObjectURL(blobUrl);
      return canvas.toDataURL("image/png");
    }

    // Raster path — fetch the bytes, return as a data URL.
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Fetches a raster image URL, downscales to `maxSize` on its longest edge, and
 * returns a PNG data URL. Preserves transparency — important for logos that
 * sit on mixed backgrounds. Sourcing via blob + createObjectURL avoids the
 * cross-origin canvas-tainting problem that plagues direct <img src=cdn/...>.
 *
 * Also normalizes format: `.jpg` files that are actually PNG (or vice versa)
 * re-encode to a known PNG, which sidesteps react-pdf's extension-based
 * format guessing and its "SOI not found in JPEG" failure mode.
 *
 * Returns null on any failure so callers can skip the <Image>.
 */
export async function fetchAndDownscaleToPng(url, maxSize = 280) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = blobUrl;
      });
      const { naturalWidth: w, naturalHeight: h } = img;
      if (!w || !h) return null;
      const scale = Math.min(1, maxSize / Math.max(w, h));
      const targetW = Math.max(1, Math.round(w * scale));
      const targetH = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      canvas.getContext("2d").drawImage(img, 0, 0, targetW, targetH);
      return canvas.toDataURL("image/png");
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch {
    return null;
  }
}

/**
 * Fetches a raster image URL, downscales to `maxSize` on its longest edge, and
 * returns a JPEG data URL. Used to keep generated PDFs small — Stormtech product
 * shots can be 500KB+ each at native resolution, and we only render them as
 * ~72pt thumbnails in the PDF. Sourcing via blob + createObjectURL avoids the
 * cross-origin canvas-tainting problem that plagues direct <img src=cdn/...>.
 *
 * Returns null on any failure so callers can skip the <Image>.
 */
export async function fetchAndDownscaleToJpeg(url, maxSize = 200, quality = 0.82) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = blobUrl;
      });

      const { naturalWidth: w, naturalHeight: h } = img;
      if (!w || !h) return null;
      const scale = Math.min(1, maxSize / Math.max(w, h));
      const targetW = Math.max(1, Math.round(w * scale));
      const targetH = Math.max(1, Math.round(h * scale));

      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      // White fill first so transparent PNGs flatten cleanly into JPEG.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, targetW, targetH);
      ctx.drawImage(img, 0, 0, targetW, targetH);

      return canvas.toDataURL("image/jpeg", quality);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch {
    return null;
  }
}
